import { ALL_FEATURES_STORAGE_KEYS, readStoredFlag, writeStoredFlag } from './labAllFeatures'
import { BOX_SHADOW_XL, GRAY_1000, GRAY_300, GRAY_500, GRAY_700, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, KeyboardEvent, useId, useState } from 'react'

import CropSquareRoundedIcon from '@mui/icons-material/CropSquareRounded'
import { LabSegmented } from './LabPanelPrimitives.component'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

type ToolsView = 'one' | 'combine'

/**
 * One tool | Combine tools, on top of the tools column it changes (Lab SDK engine, subscribers), in both views.
 * Until "Combine tools" is first picked it carries a "New" badge; on the first visit a hint explains it — answered by
 * "Try it", "Not now" or any choice, then never shown again (both remembered per browser).
 */
export const LabToolsViewSwitch: FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { isAllFeaturesView, setAllFeaturesView, isProcessingImage, isToolSwitching, isSaving } = useClientPhotoStudio()
  const hintTitleId = useId()

  const [hasSeenHint, setHasSeenHint] = useState(() => readStoredFlag(ALL_FEATURES_STORAGE_KEYS.hintSeen))
  const [hasUsedCombine, setHasUsedCombine] = useState(() => isAllFeaturesView || readStoredFlag(ALL_FEATURES_STORAGE_KEYS.used))

  const isBusy = isProcessingImage || isToolSwitching || isSaving
  const showHint = !hasSeenHint && !isAllFeaturesView && !isBusy

  const answerHint = () => {
    if (hasSeenHint) return
    setHasSeenHint(true)
    writeStoredFlag(ALL_FEATURES_STORAGE_KEYS.hintSeen, true)
  }

  const choose = (view: ToolsView, source: 'switch' | 'hint') => {
    answerHint()
    const combine = view === 'combine'
    if (combine && !hasUsedCombine) {
      setHasUsedCombine(true)
      writeStoredFlag(ALL_FEATURES_STORAGE_KEYS.used, true)
    }
    if (combine !== isAllFeaturesView) setAllFeaturesView(combine, source)
  }

  const closeHintOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') answerHint()
  }

  return (
    <Box sx={{ position: 'relative', zIndex: 3, flexShrink: 0, padding: '1.2rem 2.4rem', borderTop: `1px solid ${GRAY_300}`, backgroundColor: WHITE }}>

      <LabSegmented<ToolsView>
        large
        ariaLabel={t('lab.all_features.mode_label')}
        options={[
          { value: 'one', label: t('lab.all_features.mode_one'), icon: <CropSquareRoundedIcon /> },
          {
            value: 'combine',
            // "All tools" (2026-10-08): a noun like "One tool"; the former `mode_combine` copy stays in the catalogue
            label: t('lab.all_features.mode_all'),
            icon: <LayersOutlinedIcon />,
            adornment: hasUsedCombine ? undefined : <MUIBadge label={t('lab.stage.new')} size="xs" color="blue" />,
          },
        ]}
        value={isAllFeaturesView ? 'combine' : 'one'}
        onChange={(view) => choose(view, 'switch')}
        disabled={isBusy}
      />

      {/* First visit only: what combining gives, anchored under "Combine tools" — a darker stroke than the panels', so it
          reads as floating over the tools column */}
      {showHint && (
        <Stack
          role="dialog"
          aria-labelledby={hintTitleId}
          onKeyDown={closeHintOnEscape}
          gap="1rem"
          sx={{
            position: 'absolute',
            top: 'calc(100% + 0.6rem)',
            right: '1.6rem',
            width: '30rem',
            padding: '1.6rem',
            borderRadius: '1.2rem',
            border: `1px solid ${GRAY_500}`,
            backgroundColor: WHITE,
            boxShadow: BOX_SHADOW_XL,
          }}
        >

          <Box aria-hidden sx={{ position: 'absolute', top: '-0.7rem', right: '10.1rem', width: '1.2rem', height: '1.2rem', backgroundColor: WHITE, borderLeft: `1px solid ${GRAY_500}`, borderTop: `1px solid ${GRAY_500}`, transform: 'rotate(45deg)' }} />

          <Typography id={hintTitleId} sx={{ fontSize: '1.5rem', fontWeight: 550, lineHeight: 1.4, letterSpacing: '-0.4px', color: GRAY_1000 }}>
            {t('lab.all_features.hint.title')}
          </Typography>

          <Typography sx={{ fontSize: '1.3rem', lineHeight: 1.45, letterSpacing: '-0.2px', color: GRAY_700 }}>
            {t('lab.all_features.hint.body')}
          </Typography>

          <Stack direction="row" justifyContent="flex-end" gap="0.8rem">

            <MUIButton type="secondaryNoBorder" size="sm" onClick={answerHint}>
              {t('lab.all_features.hint.dismiss')}
            </MUIButton>

            <MUIButton type="defaultPrimary" size="sm" onClick={() => choose('combine', 'hint')}>
              {t('lab.all_features.hint.try')}
            </MUIButton>

          </Stack>

        </Stack>
      )}

    </Box>
  )
}
